import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GamesPage.css';
import { gameAPI } from '../services/api';
import { useStore } from '../store/useStore';
import GameResultModal from '../components/GameResultModal/GameResultModal';
import GameIcon from '../components/GameIcon/GameIcon';
import { useTranslation } from 'react-i18next';
import SoundManager from '../services/SoundManager';

// Импортируем правильные компоненты игр
import LunarLottery from '../components/games/LunarLottery';
import StarFlip from '../components/games/StarFlip';
import CosmicDice from '../components/games/CosmicDice';
import MeteorRace from '../components/games/MeteorRace';
import AsteroidMiner from '../components/games/AsteroidMiner';

interface Game {
  id: string;
  name: string;
  description: string;
  cost: number;
  minReward: number;
  maxReward: number;
  winChance: number;
  cooldown: number;
  isCurrentGame: boolean;
  isAvailable: boolean;
  config?: any; // Конфиг игры с шансами (для cosmic_dice и др.)
  canPlay: boolean;
  cooldownLeft: number;
  canAfford: boolean;
  stats: {
    playsToday: number;
    winsToday: number;
    profitToday: number;
    lastPlayed: string | null;
  };
}

const GamesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateBalance } = useStore();
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameError, setGameError] = useState(''); // Ошибка внутри игры
  const [isAdmin, setIsAdmin] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [forcedGame, setForcedGame] = useState<string | null>(null);
  const [dailyGame, setDailyGame] = useState<string | null>(null);
  const [testGameMode, setTestGameMode] = useState<'win' | 'lose' | 'random'>('random');
  const [showTestResult, setShowTestResult] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [gameResult, setGameResult] = useState<any>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  // State для активного буста
  const [activeBoost, setActiveBoost] = useState<{
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null>(null);
  const [boostTimeLeft, setBoostTimeLeft] = useState(0);

  // Загрузка активного буста
  const loadBoostStatus = async () => {
    try {
      const response = await gameAPI.getBoostStatus();
      if (response.data.success && response.data.data.active) {
        const boost = response.data.data;
        setActiveBoost({
          multiplier: boost.multiplier,
          timeLeft: boost.remainingSeconds,
          expiresAt: boost.expires_at
        });
        setBoostTimeLeft(boost.remainingSeconds);
      } else {
        setActiveBoost(null);
        setBoostTimeLeft(0);
      }
    } catch (error) {
      console.error('Ошибка загрузки статуса буста:', error);
    }
  };

  useEffect(() => {
    loadGames();
    loadBoostStatus(); // Загружаем буст при монтировании
    const interval = setInterval(loadGames, 30000); // Обновляем каждые 30 секунд
    return () => clearInterval(interval);
  }, []);

  // Таймер для активного буста
  useEffect(() => {
    if (!activeBoost || boostTimeLeft <= 0) return;

    const timer = setInterval(() => {
      setBoostTimeLeft(prev => {
        if (prev <= 1) {
          // Буст истек - перезагружаем статус
          loadBoostStatus();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [activeBoost, boostTimeLeft]);

  const loadGames = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await gameAPI.getAllGames();
      
      if (response.data.success) {
        console.log('📊 API Response:', {
          isAdmin: response.data.isAdmin,
          testMode: response.data.testMode,
          gamesCount: response.data.games?.length,
          userBalance: response.data.userBalance
        });

        setGames(response.data.games);
        setIsAdmin(response.data.isAdmin || false);
        setTestMode(response.data.testMode);
        setForcedGame(response.data.forcedGame);
        setDailyGame(response.data.dailyGame);

        // ❌ НЕ ОБНОВЛЯЕМ БАЛАНС ИЗ /minigames/all!
        // Мини-игры НЕ меняют основной баланс пользователя.
        // Баланс обновляется только при реальных операциях: claim, deposit, withdraw
        // Проблема была: FloatingGameButton вызывает getAllGames() каждые 5 сек БЕЗ токена → balance=0
      } else {
        setError(response.data.message || t('gamesPage.errors.loadFailed'));
      }
    } catch (error: any) {
      console.error(t('gamesPage.errors.loadGames'), error);
      setError(t('gamesPage.errors.connectionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGameSelect = (gameId: string, event?: React.MouseEvent) => {
    console.log('🎮 BUTTON CLICKED! Game ID:', gameId);
    console.log('Event:', event);
    
    // Останавливаем всплытие события
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const game = games.find(g => g.id === gameId);
    console.log('Game found:', game);
    
    if (game) {
      console.log('✅ Setting selected game:', gameId);
      setSelectedGame(gameId);
    } else {
      console.log('❌ Game not found!');
    }
  };

  // Тестовый запуск игры для админа
  const handleTestGame = async (gameId: string) => {
    try {
      setIsPlaying(true);
      setError('');
      
      const response = await gameAPI.testMinigame({
        gameType: gameId,
        mode: testGameMode,
        userId: '592457038' // Admin ID
      });

      if (response.data.success) {
        setTestResult(response.data);
        setShowTestResult(true);
        
        // Обновляем баланс если есть изменения
        if (response.data.newBalance !== undefined) {
          updateBalance(response.data.newBalance);
        }
        
        // Перезагружаем игры через 3 секунды
        setTimeout(() => {
          loadGames();
          setShowTestResult(false);
          setTestResult(null);
        }, 5000);
      } else {
        setError(response.data.message || t('gamesPage.errors.testingFailed'));
      }
    } catch (error: any) {
      console.error(t('gamesPage.errors.testingError'), error);
      setError(t('gamesPage.errors.testRunFailed'));
    } finally {
      setIsPlaying(false);
    }
  };

  // Удалена неиспользуемая функция handleGameResult - используется handleGamePlay вместо неё

  const renderGame = () => {
    if (!selectedGame) return null;
    
    const game = games.find(g => g.id === selectedGame);
    if (!game) return null;

    // Общий обработчик для всех игр
    const handleGamePlay = async (choice?: any) => {
      try {
        setIsPlaying(true);
        setGameError(''); // Очищаем предыдущую ошибку
        setGameResult(null); // Сбрасываем результат чтобы анимация сработала при новой игре

        // Звук начала игры (для всех игр кроме cosmic_dice - там свой звук)
        if (selectedGame !== 'cosmic_dice') {
          SoundManager.playGameStart();
        }

        const response = await gameAPI.playMiniGameBoost({
          gameType: selectedGame,
          choice: choice || null,
          mode: testMode ? testGameMode : undefined
        });

        if (response.data.success) {
          // ✅ МИНИ-ИГРЫ НЕ МЕНЯЮТ ОСНОВНОЙ БАЛАНС!
          // Они дают буст для фарминга, а не деньги на баланс
          // Победа в мини-игре -> буст в прогресс накопления -> claim -> копилка -> баланс

          // Сохраняем результат для модального окна
          setGameResult(response.data.data);

          // Перезагружаем статус буста после игры
          loadBoostStatus();

          // Для игры в кости - результат показывается внутри самой игры, модалку НЕ показываем
          if (selectedGame === 'cosmic_dice') {
            // CosmicDice сам показывает результат внутри, модалку не открываем
            // Игра остается открытой для повторной игры
          } else {
            // Для других игр - показываем модальное окно с результатом и закрываем игру
            // Воспроизводим звук результата
            const gameResultData = response.data.data?.gameResult;
            if (gameResultData?.won) {
              SoundManager.playGameWin();
            } else {
              SoundManager.playGameLoss();
            }
            setShowResultModal(true);
            setSelectedGame(null);
          }

          // Обновляем список игр
          loadGames();
        } else {
          // Ошибка от сервера - не закрываем игру
          setGameError(response.data.message || t('gamesPage.errors.playError'));
        }
      } catch (error: any) {
        console.error(t('gamesPage.errors.playFailed'), error);
        // Ошибка сети/запроса - не закрываем игру
        setGameError(error.response?.data?.message || t('gamesPage.errors.saveFailed'));
      } finally {
        setIsPlaying(false);
      }
    };
    
    // Единые пропсы для всех игр
    const gameProps = {
      onPlay: handleGamePlay,
      onClose: () => {
        setSelectedGame(null);
        setGameError(''); // Очищаем ошибку при закрытии
        setGameResult(null); // Очищаем результат при закрытии
      },
      cost: game.cost,
      minReward: game.minReward,
      maxReward: game.maxReward,
      error: gameError, // Передаем ошибку в игру
      isPlaying: isPlaying,
      activeBoost: activeBoost && boostTimeLeft > 0 ? {
        multiplier: activeBoost.multiplier,
        timeLeft: boostTimeLeft,
        expiresAt: activeBoost.expiresAt
      } : null
    };

    switch (selectedGame) {
      case 'lunar_lottery':
        return <LunarLottery {...gameProps} config={game.config} />;
      case 'star_flip':
        return <StarFlip {...gameProps} config={game.config} />;
      case 'cosmic_dice':
        // Для космических костей передаем результат игры и config с шансами
        return <CosmicDice
          {...gameProps}
          config={game.config} // Передаём конфиг с шансами из API
          result={gameResult?.gameResult ? {
            won: gameResult.gameResult.won,
            dice1: gameResult.gameResult.dice1,
            dice2: gameResult.gameResult.dice2,
            multiplier: gameResult.boost?.multiplier,
            duration: gameResult.boost?.duration
          } : undefined}
        />;
      case 'meteor_race':
        return <MeteorRace {...gameProps} config={game.config} />;
      case 'asteroid_miner':
        return <AsteroidMiner {...gameProps} config={game.config} />;
      default:
        return null;
    }
  };

  // Маппинг ID игр на иконки - теперь используем GameIcon компонент

  // Хелпер для получения локализованных названий игр
  const getGameName = (gameId: string): string => {
    const idMap: Record<string, string> = {
      'lunar_lottery': 'lunarLottery',
      'star_flip': 'starFlip',
      'cosmic_dice': 'cosmicDice',
      'meteor_race': 'meteorRace',
      'asteroid_miner': 'asteroidMiner'
    };
    const key = idMap[gameId];
    return key ? t(`games.miniGamesList.${key}Title`) : gameId;
  };

  const getGameDesc = (gameId: string): string => {
    const idMap: Record<string, string> = {
      'lunar_lottery': 'lunarLottery',
      'star_flip': 'starFlip',
      'cosmic_dice': 'cosmicDice',
      'meteor_race': 'meteorRace',
      'asteroid_miner': 'asteroidMiner'
    };
    const key = idMap[gameId];
    return key ? t(`games.miniGamesList.${key}Desc`) : '';
  };

  const formatCooldown = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}${t('gamesPage.time.hours')} ${minutes}${t('gamesPage.time.minutes')}`;
    } else if (minutes > 0) {
      return `${minutes}${t('gamesPage.time.minutes')} ${secs}${t('gamesPage.time.seconds')}`;
    } else {
      return `${secs}${t('gamesPage.time.seconds')}`;
    }
  };

  // Изменение форсированной игры (только для админа в тест-режиме)
  const handleForceGameChange = async (gameId: string) => {
    if (!isAdmin) return;
    
    try {
      const response = await gameAPI.updateSetting('minigames', 'forced_game', gameId);
      if (response.data.success) {
        await loadGames();
      }
    } catch (error) {
      console.error(t('gamesPage.errors.gameChangeFailed'), error);
    }
  };

  if (loading) {
    return (
      <div className="games-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('gamesPage.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="games-page">
        <div className="error-state">
          <p className="error-message">❌ {error}</p>
          <button className="retry-button" onClick={loadGames}>
            {t('gamesPage.retryButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="games-page">
      <div className="page-content">
        <h1 className="page-title">{t('gamesPage.title')}</h1>

        {/* Баланс пользователя */}
        <div className="user-balance">
          <span className="balance-label">{t('gamesPage.balance')}</span>
          <span className="balance-value">
            {isAdmin ? '1,000,000.00' : (user?.balance || 0).toFixed(2)} SPERM
            {isAdmin && <span style={{ fontSize: '12px', opacity: 0.7 }}> {t('gamesPage.balanceTestMode')}</span>}
          </span>
        </div>

        {/* Индикатор активного буста */}
        {activeBoost && boostTimeLeft > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginTop: '12px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
            animation: 'pulseGlow 2s ease-in-out infinite'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>⚡</span>
              <div>
                <div style={{ fontWeight: 'bold', color: 'white', fontSize: '16px' }}>
                  {t('games.activeBoostTitle', { multiplier: activeBoost.multiplier })}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('games.incomeMultiplied', { multiplier: activeBoost.multiplier })}
                </div>
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              {Math.floor(boostTimeLeft / 60)}:{(boostTimeLeft % 60).toString().padStart(2, '0')}
            </div>
          </div>
        )}

        {/* Результат тестирования для админа */}
        {showTestResult && testResult && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: testResult.won ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            zIndex: 1000,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            minWidth: '300px',
            textAlign: 'center'
          }}>
            <h2 style={{ margin: '0 0 16px 0' }}>
              {testResult.won ? t('gamesPage.testResult.win') : t('gamesPage.testResult.lose')}
            </h2>
            <p style={{ fontSize: '18px', margin: '0 0 8px 0' }}>
              {t('gamesPage.testResult.game')} {testResult.game}
            </p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
              {testResult.won ? '+' : ''}{testResult.boost_percent || 0}% {t('gamesPage.testResult.boost')}
            </p>
            {testResult.boost_duration && (
              <p style={{ fontSize: '16px', margin: '0 0 16px 0' }}>
                {t('gamesPage.testResult.duration')} {testResult.boost_duration} {t('gamesPage.testResult.durationUnit')}
              </p>
            )}
            {testResult.details && (
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.2)', 
                padding: '12px', 
                borderRadius: '8px',
                marginTop: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {JSON.stringify(testResult.details)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Панель тестирования для админа */}
        {isAdmin && (
          <div className="admin-test-panel" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            color: 'white'
          }}>
            <h3 style={{ margin: '0 0 12px 0' }}>{t('gamesPage.adminPanel.title')}</h3>

            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                {t('gamesPage.adminPanel.testModeActive')}
              </p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', fontSize: '14px' }}>
                <li>{t('gamesPage.adminPanel.features.allGamesAvailable')}</li>
                <li>{t('gamesPage.adminPanel.features.noCooldowns')}</li>
                <li>{t('gamesPage.adminPanel.features.playUnlimited')}</li>
              </ul>
            </div>
            
            {/* Режим тестирования */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                {t('gamesPage.adminPanel.testMode.title')}
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setTestGameMode('win')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: testGameMode === 'win' ? '#10b981' : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: testGameMode === 'win' ? 'bold' : 'normal'
                  }}
                >
                  {t('gamesPage.adminPanel.testMode.win')}
                </button>
                <button
                  onClick={() => setTestGameMode('lose')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: testGameMode === 'lose' ? '#ef4444' : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: testGameMode === 'lose' ? 'bold' : 'normal'
                  }}
                >
                  {t('gamesPage.adminPanel.testMode.lose')}
                </button>
                <button
                  onClick={() => setTestGameMode('random')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: testGameMode === 'random' ? '#8b5cf6' : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: testGameMode === 'random' ? 'bold' : 'normal'
                  }}
                >
                  {t('gamesPage.adminPanel.testMode.random')}
                </button>
              </div>
              <p style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                {t('gamesPage.adminPanel.testMode.selectedMode')} <strong>{
                  testGameMode === 'win' ? t('gamesPage.adminPanel.testMode.alwaysWin') :
                  testGameMode === 'lose' ? t('gamesPage.adminPanel.testMode.alwaysLose') :
                  t('gamesPage.adminPanel.testMode.randomResult')
                }</strong>
              </p>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ marginRight: '8px' }}>{t('gamesPage.adminPanel.forceGame')}</label>
              <select
                value={forcedGame || ''}
                onChange={(e) => handleForceGameChange(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'white',
                  color: '#333'
                }}
              >
                <option value="">{t('gamesPage.adminPanel.autoRotation')} {dailyGame})</option>
                {games.map(game => (
                  <option key={game.id} value={game.id}>
                    {getGameName(game.id)}
                  </option>
                ))}
              </select>
            </div>

            {forcedGame && (
              <div style={{
                background: 'rgba(255, 255, 0, 0.2)',
                padding: '8px',
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <p style={{ margin: '0' }}>
                  {t('gamesPage.adminPanel.forceWarning.title')} <strong>{forcedGame ? getGameName(forcedGame) : ''}</strong>
                </p>
                <p style={{ margin: '4px 0 0 0', opacity: 0.8 }}>
                  {t('gamesPage.adminPanel.forceWarning.description')}
                </p>
              </div>
            )}
            
            <div style={{
              marginTop: '12px',
              padding: '8px',
              background: 'rgba(0, 255, 0, 0.1)',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <strong>{t('gamesPage.adminPanel.howToTest.title')}</strong>
              <ol style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                <li>{t('gamesPage.adminPanel.howToTest.step1')}</li>
                <li>{t('gamesPage.adminPanel.howToTest.step2')}</li>
                <li>{t('gamesPage.adminPanel.howToTest.step3')}</li>
              </ol>
            </div>
          </div>
        )}
        
        {/* Если игра выбрана, показываем её */}
        {selectedGame ? (
          <div className="game-container">
            {renderGame()}
          </div>
        ) : (
          <>
            {/* Игра дня - prominently displayed */}
            {games.filter(g => g.isCurrentGame).map(game => (
              <div key={game.id} className="game-of-day-section" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                padding: '24px',
                marginBottom: '24px',
                color: 'white',
                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ margin: 0, flex: 1 }}>{t('gamesPage.gameOfDay.title')}</h2>
                  <span style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {t('gamesPage.gameOfDay.availableToday')}
                  </span>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px',
                      color: 'white'
                    }}>
                      <GameIcon gameId={game.id} />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>{getGameName(game.id)}</h3>
                      <p style={{ margin: '0 0 12px 0', opacity: 0.9 }}>{getGameDesc(game.id)}</p>


                      <div style={{ display: 'flex', gap: '24px' }}>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '14px' }}>{t('gamesPage.gameOfDay.boostLabel')}</span>
                          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{t('gamesPage.gameOfDay.boostValue')}</div>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '14px' }}>{t('gamesPage.gameOfDay.durationLabel')}</span>
                          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{t('gamesPage.gameOfDay.durationValue')}</div>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8, fontSize: '14px' }}>{t('gamesPage.gameOfDay.winChanceLabel')}</span>
                          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{(game.winChance * 100).toFixed(0)}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Статистика за сегодня */}
                  {game.stats.playsToday > 0 && (
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span>{t('gamesPage.gameOfDay.stats.playedToday')} <strong>{game.stats.playsToday}</strong></span>
                        <span>{t('gamesPage.gameOfDay.stats.wins')} <strong>{game.stats.winsToday}</strong></span>
                        <span>{t('gamesPage.gameOfDay.stats.totalPrize')} <strong>{game.stats.profitToday > 0 ? '+' : ''}{game.stats.profitToday.toFixed(2)} SPERM</strong></span>
                      </div>
                    </div>
                  )}
                  
                  <button
                    className="play-game-of-day-btn game-action-btn"
                    onClick={() => {
                      console.log('🎯 PLAY NOW CLICKED for game:', game.id);
                      handleGameSelect(game.id);
                    }}
                  >
                    {!isAdmin && game.cooldownLeft > 0 ? (
                      `${t('gamesPage.gameOfDay.cooldownButton')} ${formatCooldown(game.cooldownLeft)}`
                    ) : !isAdmin && !game.canAfford ? (
                      t('gamesPage.gameOfDay.insufficientFunds')
                    ) : (
                      t('gamesPage.gameOfDay.playButton')
                    )}
                  </button>
                </div>
              </div>
            ))}

            {/* Покер - отдельная игра */}
            <div className="poker-section" style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '20px',
              padding: '24px',
              marginBottom: '24px',
              color: 'white',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 215, 0, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, flex: 1, fontSize: '22px' }}>
                  🃏 {t('gamesPage.poker.title', 'Texas Hold\'em Poker')}
                </h2>
                <span style={{
                  background: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)',
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#000'
                }}>
                  {t('gamesPage.poker.newBadge', 'NEW!')}
                </span>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #2d5016 0%, #1a3009 100%)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '40px',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    🎰
                  </div>

                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>
                      {t('gamesPage.poker.subtitle', 'Играй с ботами или реальными игроками')}
                    </h3>
                    <p style={{ margin: '0 0 12px 0', opacity: 0.9, fontSize: '14px' }}>
                      {t('gamesPage.poker.description', 'Классический техасский холдем. Выигрывай монеты, побеждая других игроков!')}
                    </p>

                    <div style={{ display: 'flex', gap: '24px' }}>
                      <div>
                        <span style={{ opacity: 0.8, fontSize: '12px' }}>{t('gamesPage.poker.minBet', 'Мин. ставка')}</span>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffd700' }}>1 🌙</div>
                      </div>
                      <div>
                        <span style={{ opacity: 0.8, fontSize: '12px' }}>{t('gamesPage.poker.rake', 'Рейк')}</span>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ef4444' }}>5%</div>
                      </div>
                      <div>
                        <span style={{ opacity: 0.8, fontSize: '12px' }}>{t('gamesPage.poker.players', 'Игроки')}</span>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>2-6</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '20px'
                }}>
                  <button
                    onClick={() => navigate('/poker')}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '16px 24px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    🤖 {t('gamesPage.poker.playBots', 'Играть с ботами')}
                  </button>
                  <button
                    onClick={() => navigate('/poker')}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '16px 24px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                    }}
                  >
                    👥 {t('gamesPage.poker.pvp', 'Мультиплеер')}
                  </button>
                </div>
              </div>
            </div>

            {/* Список всех игр - только для админа */}
            {isAdmin && (
              <>
                <h2 style={{ margin: '24px 0 16px 0' }}>{t('gamesPage.allGames')}</h2>
                <div className="games-grid">
                  {games.map(game => {
                    const isToday = game.isCurrentGame;
                    // Для админа все игры всегда доступны
                    const canPlay = true;
                    const isAvailable = true;
                
                return (
                  <div 
                    key={game.id} 
                    className={`game-card ${isToday ? 'today-game' : ''}`}
                    style={{
                      opacity: 1,
                      position: 'relative',
                      pointerEvents: 'auto',
                      zIndex: 1
                    }}
                  >
                    {isToday && !isAdmin && (
                      <div className="today-badge" style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 1
                      }}>
                        {t('gamesPage.gameCard.todayBadge')}
                      </div>
                    )}

                    <div className="game-icon">
                      <GameIcon gameId={game.id} />
                    </div>
                    <h3 className="game-name">{getGameName(game.id)}</h3>
                    <p className="game-description">{getGameDesc(game.id)}</p>
                    
                    <div className="game-info">
                      <div className="game-reward">
                        <span className="reward-label">{t('gamesPage.gameCard.boostLabel')}</span>
                        <span className="reward-value">{t('gamesPage.gameCard.boostValue')}</span>
                      </div>
                      <div className="game-cost">
                        <span className="cost-label">{t('gamesPage.gameCard.costLabel')}</span>
                        <span className="cost-value" style={{ color: '#10b981' }}>{t('gamesPage.gameCard.costValue')}</span>
                      </div>
                    </div>
                    
                    {/* Статистика игры за сегодня */}
                    {game.stats.playsToday > 0 && (
                      <div className="game-stats" style={{
                        fontSize: '12px',
                        marginTop: '8px',
                        padding: '8px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '8px'
                      }}>
                        <div>{t('gamesPage.gameCard.stats.played')} {game.stats.playsToday} | {t('gamesPage.gameCard.stats.wins')} {game.stats.winsToday}</div>
                        <div>{t('gamesPage.gameCard.stats.profit')} {game.stats.profitToday > 0 ? '+' : ''}{game.stats.profitToday.toFixed(2)} SPERM</div>
                      </div>
                    )}
                    
                    {/* Кнопки для админа */}
                    {isAdmin ? (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          className="game-play-btn"
                          onClick={(e) => {
                            console.log('🔥 Play button CLICK for game:', game.id);
                            handleGameSelect(game.id);
                          }}
                        >
                          {t('gamesPage.gameCard.playButton')}
                        </button>
                        <button
                          className="game-test-btn"
                          onClick={(e) => {
                            console.log('🧪 Test button CLICK for game:', game.id);
                            handleTestGame(game.id);
                          }}
                        >
                          {isPlaying ? t('gamesPage.gameCard.testingButton') : t('gamesPage.gameCard.testButton')}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="game-play-btn"
                        onClick={(e) => {
                          console.log('🎯 Regular play button clicked for game:', game.id);
                          handleGameSelect(game.id);
                        }}
                      >
                        {t('gamesPage.gameCard.regularPlayButton')}
                      </button>
                    )}
                  </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Информация о системе игр */}
            <div className="games-info" style={{
              marginTop: '32px',
              padding: '16px',
              background: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '12px'
            }}>
              <h3 style={{ marginTop: 0 }}>{t('gamesPage.info.title')}</h3>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>{t('gamesPage.info.rule1')}</li>
                <li>{t('gamesPage.info.rule2')}</li>
                <li>{t('gamesPage.info.rule3')}</li>
                <li>{t('gamesPage.info.rule4')}</li>
                <li>{t('gamesPage.info.rule5')}</li>
                <li>{t('gamesPage.info.rule6')}</li>
                {isAdmin && <li style={{ color: '#667eea', fontWeight: 'bold' }}>{t('gamesPage.info.adminRule')}</li>}
              </ul>
            </div>
          </>
        )}
      </div>
      
      {/* Модальное окно с результатом игры */}
      <GameResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        result={gameResult}
      />
    </div>
  );
};

export default GamesPage;